<script setup lang="ts">
import { computed, watch } from 'vue';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DatePicker from '@/components/DatePicker/DatePicker.vue';
import ScheduleFormField from './ScheduleFormField.vue';
import ScheduleOrderingSelect from './ScheduleOrderingSelect.vue';
import ScheduleDayPicker from './ScheduleDayPicker.vue';
import ScheduleTypeIcon from './ScheduleTypeIcon.vue';
import TimeOfDayInput from './TimeOfDayInput.vue';
import {
    SCHEDULE_KIND_LABELS,
    scheduleKindColor,
    bulkTabEndsBeforeStart,
    bulkTabSetStartDate,
    formEndsBeforeStart,
    formOccurrence,
    kindAllowsNoEndDate,
    kindHasOccurrenceChoice,
    kindUsesOrdering,
    kindUsesPriority,
    timesCrossMidnight,
    type BulkScheduleField,
    type EditableScheduleKind,
    type ScheduleBulkState,
    type ScheduleForm,
} from '@/utils/customPlaylistSchedule';
import { END_BEFORE_START_MESSAGE } from '@/utils/date';

/**
 * The schedule editor's fields. The Edit Schedule dialog shows them for one schedule, and the
 * bulk edit dialog shows them once per kind of schedule in the selection.
 *
 * For one schedule (`mode="single"`) every field edits the form. In a bulk edit
 * (`mode="bulk"`) every field starts as "Keep as is" and only the fields the user changes are
 * sent. Bulk edit also offers a few things the single editor has no need for: moving a date by
 * some days, no priority, and the rarely used fields under "More".
 *
 * The type is the tab in a bulk edit, and the occurrence cannot change there: switching
 * between weekly and dated would move a row's dates to other columns.
 */
const props = withDefaults(
    defineProps<{
        mode?: 'single' | 'bulk';
        /** The types the single editor may switch between. */
        kindOptions?: EditableScheduleKind[];
        kindIsEditable?: boolean;
        readOnly?: boolean;
        /** The message for the first of these fields that has a problem. */
        errorFor?: (...fields: string[]) => string | null;
        schedulingModeText?: string | null;
        stationChangeText?: string | null;
    }>(),
    {
        mode: 'single',
        kindOptions: () => [],
        kindIsEditable: true,
        readOnly: false,
        errorFor: () => null,
        schedulingModeText: null,
        stationChangeText: null,
    },
);

const form = defineModel<ScheduleForm>('form', { required: true });
const bulkState = defineModel<ScheduleBulkState | null>('bulk', { default: null });

const isBulk = computed(() => props.mode === 'bulk' && bulkState.value !== null);

// The latest values, so two changes before the parent re-renders both count. The models only
// catch up on that render, and a second change built on them would drop the first.
let latestForm: ScheduleForm = form.value;
let latestBulk: ScheduleBulkState | null = bulkState.value;
watch(form, (value) => {
    latestForm = value;
});
watch(bulkState, (value) => {
    latestBulk = value;
});

function update(patch: Partial<ScheduleForm>): void {
    latestForm = { ...latestForm, ...patch };
    form.value = latestForm;
}

function updateBulk(patch: Partial<ScheduleBulkState>): void {
    if (!latestBulk) return;
    latestBulk = { ...latestBulk, ...patch };
    bulkState.value = latestBulk;
}

function isChanged(field: BulkScheduleField): boolean {
    return bulkState.value?.changed[field] ?? false;
}

function setChanged(field: BulkScheduleField, changed: boolean): void {
    if (latestBulk) updateBulk({ changed: { ...latestBulk.changed, [field]: changed } });
}

function onKindChange(value: unknown): void {
    const kind = value as EditableScheduleKind;
    update({ kind, noEndDate: kind === 'oneoff' ? false : latestForm.noEndDate });
}

const occurrence = computed(() => formOccurrence(form.value));
const showsOccurrence = computed(() => !isBulk.value && kindHasOccurrenceChoice(form.value.kind));
const showsDays = computed(() => occurrence.value === 'recurring' && (!isBulk.value || form.value.kind === 'recurring'));
const showsNoEndDate = computed(() => form.value.kind !== 'oneoff');
const showsPriority = computed(() => kindUsesPriority(form.value.kind));
const showsOrdering = computed(() => kindUsesOrdering(form.value.kind));
const showsInsert = computed(() => form.value.kind === 'insert');
const showsMinutes = computed(() => form.value.kind === 'message' || form.value.kind === 'silence');
/**
 * Only a daily window rolls over midnight: a repeating schedule, or a message or
 * silence (gvplayer checks the messaging window each day). A dated one-off or
 * insert ends on its end date, so there the end date field already says when.
 */
const endsNextDay = computed(
    () =>
        (occurrence.value === 'recurring' || form.value.kind === 'message' || form.value.kind === 'silence') &&
        timesCrossMidnight(form.value.startTime, form.value.endTime),
);

/** A number field: blank reads as null so validation can name it. */
function numberOrNull(event: Event): number | null {
    const raw = (event.target as HTMLInputElement).value.trim();
    if (raw === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
}

function setNumber(field: 'priority' | 'repeatMinutes', event: Event): void {
    update({ [field]: numberOrNull(event) });
}

function setCount(field: 'repeatWeeks' | 'fromTracks' | 'toTracks' | 'rampDays', event: Event): void {
    // Validation rejects anything below 1; NaN is kept as 0 so it is named rather than silently replaced.
    update({ [field]: numberOrNull(event) ?? 0 });
}

function setShift(field: 'startShiftDays' | 'endShiftDays', event: Event): void {
    updateBulk({ [field]: numberOrNull(event) });
}

/** What a bulk edit can do to each date. A one-off always has an end date. */
const startDateActions = [
    { value: 'set', label: 'Set to' },
    { value: 'shift', label: 'Move by days' },
] as const;
const endDateActions = computed(() => [
    { value: 'set', label: 'Set to' },
    { value: 'shift', label: 'Move by days' },
    ...(kindAllowsNoEndDate(form.value.kind as EditableScheduleKind) ? [{ value: 'clear', label: 'No end date' }] : []),
]);

const YES_NO = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
] as const;

const fieldClass = 'flex flex-col gap-1.5';
const labelClass = 'text-sm font-medium';
const hintClass = 'text-muted-foreground text-xs';
const errorClass = 'text-destructive text-xs';

/**
 * The message under the end date. An end before the start shows as soon as it happens, not
 * only after Save: the end date picker cannot pick a day before the start, but a start moved
 * past the end gets there anyway. Save still refuses it through the editor's own checks. In
 * the single editor, a schedule saved with its end already before its start shows nothing
 * until the user changes its dates or times (see formEndsBeforeStart).
 */
const endDateMessage = computed(() => {
    const endsBeforeStart = isBulk.value
        ? bulkTabEndsBeforeStart({ form: form.value, bulk: bulkState.value! })
        : formEndsBeforeStart(form.value);
    return props.errorFor('endDate') ?? (endsBeforeStart ? END_BEFORE_START_MESSAGE : null);
});

/** The first day the end date picker offers. A bulk edit has one only when it sets the start date. */
const endDateMin = computed(() => (isBulk.value ? bulkTabSetStartDate({ form: form.value, bulk: bulkState.value! }) : form.value.startDate));
</script>

<template>
    <slot name="before" />

    <div v-if="!isBulk" :class="fieldClass">
        <label for="schedule-kind" :class="labelClass">Schedule type</label>
        <Select v-if="kindOptions.length > 1 && kindIsEditable" :model-value="form.kind" @update:model-value="onKindChange">
            <SelectTrigger id="schedule-kind" class="w-full" data-testid="schedule-kind">
                <!-- The chosen type reads the same closed as it does in the open menu. -->
                <SelectValue>
                    <span class="flex items-center gap-2">
                        <span class="h-4 w-1 shrink-0 rounded-sm" :style="{ backgroundColor: scheduleKindColor(form.kind) }" aria-hidden="true" data-testid="schedule-kind-color" />
                        <ScheduleTypeIcon :kind="form.kind" decorative />
                        {{ SCHEDULE_KIND_LABELS[form.kind] }}
                    </span>
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                <!-- Each type with the colour bar and icon it is drawn with on the cards. -->
                <SelectItem v-for="kind in kindOptions" :key="kind" :value="kind" :data-kind="kind">
                    <span class="flex items-center gap-2">
                        <span class="h-4 w-1 shrink-0 rounded-sm" :style="{ backgroundColor: scheduleKindColor(kind) }" aria-hidden="true" data-testid="schedule-kind-color" />
                        <ScheduleTypeIcon :kind="kind" decorative />
                        {{ SCHEDULE_KIND_LABELS[kind] }}
                    </span>
                </SelectItem>
            </SelectContent>
        </Select>
        <p v-else id="schedule-kind" class="bg-muted flex items-center gap-2 rounded-md px-3 py-2 text-sm" data-testid="schedule-kind-fixed">
            <span class="h-4 w-1 shrink-0 rounded-sm" :style="{ backgroundColor: scheduleKindColor(form.kind) }" aria-hidden="true" />
            <ScheduleTypeIcon :kind="form.kind" decorative />
            {{ SCHEDULE_KIND_LABELS[form.kind] }}
        </p>
        <p v-if="stationChangeText" :class="hintClass" data-testid="schedule-station-change-target">
            {{ stationChangeText }}
        </p>
    </div>

    <ScheduleFormField
        v-if="showsOrdering"
        label="Ordering"
        for-id="schedule-ordering"
        name="ordering"
        :bulk="isBulk"
        :changed="isChanged('ordering')"
        @update:changed="(value) => setChanged('ordering', value)"
    >
        <ScheduleOrderingSelect id="schedule-ordering" :model-value="form.ordering" :disabled="readOnly" @update:model-value="(value) => update({ ordering: value! })" />
    </ScheduleFormField>

    <ScheduleFormField
        v-if="showsPriority"
        label="Priority"
        for-id="schedule-priority"
        name="priority"
        :bulk="isBulk"
        :changed="isChanged('priority')"
        @update:changed="(value) => setChanged('priority', value)"
    >
        <Input
            id="schedule-priority"
            type="number"
            min="1"
            step="1"
            placeholder="None"
            :disabled="isBulk && bulkState!.priorityNone"
            :model-value="form.priority ?? ''"
            :aria-invalid="errorFor('priority') ? 'true' : undefined"
            data-testid="schedule-priority"
            @input="setNumber('priority', $event)"
        />
        <div v-if="isBulk" class="flex items-center gap-1.5 text-sm">
            <Checkbox
                id="schedule-bulk-priority-none"
                :model-value="bulkState!.priorityNone"
                data-testid="schedule-bulk-priority-none"
                @update:model-value="(value) => updateBulk({ priorityNone: value === true })"
            />
            <label for="schedule-bulk-priority-none" class="cursor-pointer">No priority</label>
        </div>
        <p :class="hintClass" data-testid="schedule-priority-hint">
            Priority only takes effect when this zone uses Priority scheduling.
            <span v-if="schedulingModeText" data-testid="schedule-priority-mode">{{ schedulingModeText }}</span>
        </p>
        <p v-if="errorFor('priority')" :class="errorClass">{{ errorFor('priority') }}</p>
    </ScheduleFormField>

    <div v-if="showsOccurrence" :class="fieldClass">
        <label for="schedule-occurrence" :class="labelClass">Occurrence</label>
        <Select :model-value="form.occurrence" :disabled="readOnly" @update:model-value="(value) => update({ occurrence: value as ScheduleForm['occurrence'] })">
            <SelectTrigger id="schedule-occurrence" class="w-full" data-testid="schedule-occurrence">
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="recurring">Recurring</SelectItem>
                <SelectItem value="single">One-off</SelectItem>
            </SelectContent>
        </Select>
        <p v-if="errorFor('occurrence')" :class="errorClass">{{ errorFor('occurrence') }}</p>
    </div>

    <template v-if="showsInsert">
        <ScheduleFormField
            label="Frequency"
            name="frequency"
            :bulk="isBulk"
            :changed="isChanged('frequency')"
            @update:changed="(value) => setChanged('frequency', value)"
        >
            <div class="flex items-center gap-2 text-sm">
                <span>From every</span>
                <Input
                    type="number"
                    min="1"
                    class="w-20"
                    aria-label="From every (tracks)"
                    :model-value="form.fromTracks"
                    data-testid="schedule-from-tracks"
                    @input="setCount('fromTracks', $event)"
                />
                <span>to every</span>
                <Input
                    type="number"
                    min="1"
                    class="w-20"
                    aria-label="To every (tracks)"
                    :model-value="form.toTracks"
                    data-testid="schedule-to-tracks"
                    @input="setCount('toTracks', $event)"
                />
                <span>tracks</span>
            </div>
            <p v-if="errorFor('fromTracks', 'toTracks', 'frequency')" :class="errorClass">{{ errorFor('fromTracks', 'toTracks', 'frequency') }}</p>
        </ScheduleFormField>
        <ScheduleFormField
            label="Ramp (days)"
            for-id="schedule-ramp"
            name="rampDays"
            :bulk="isBulk"
            :changed="isChanged('rampDays')"
            @update:changed="(value) => setChanged('rampDays', value)"
        >
            <Input id="schedule-ramp" type="number" min="1" :model-value="form.rampDays" data-testid="schedule-ramp" @input="setCount('rampDays', $event)" />
            <p v-if="errorFor('rampDays')" :class="errorClass">{{ errorFor('rampDays') }}</p>
        </ScheduleFormField>
    </template>

    <ScheduleFormField
        v-if="showsMinutes"
        label="Minutes between plays"
        for-id="schedule-minutes"
        name="repeatMinutes"
        :bulk="isBulk"
        :changed="isChanged('repeatMinutes')"
        @update:changed="(value) => setChanged('repeatMinutes', value)"
    >
        <Input
            id="schedule-minutes"
            type="number"
            min="1"
            :placeholder="form.kind === 'silence' && !isBulk ? 'Optional' : ''"
            :model-value="form.repeatMinutes ?? ''"
            data-testid="schedule-minutes"
            @input="setNumber('repeatMinutes', $event)"
        />
        <p v-if="errorFor('repeatMinutes')" :class="errorClass">{{ errorFor('repeatMinutes') }}</p>
    </ScheduleFormField>

    <!-- Start and end time share one row for every kind, as tabasco-react lays them
         out, and the end time wraps onto its own line when both do not fit. Each
         time input keeps its natural width, so nothing overlaps. -->
    <div class="flex flex-wrap gap-x-6 gap-y-4 sm:col-span-2" data-testid="schedule-time-row">
        <ScheduleFormField
            class="shrink-0"
            label="Start time"
            name="startTime"
            :bulk="isBulk"
            :changed="isChanged('startTime')"
            @update:changed="(value) => setChanged('startTime', value)"
        >
            <TimeOfDayInput
                :model-value="form.startTime"
                label="Start time"
                :invalid="Boolean(errorFor('startTime'))"
                :disabled="readOnly"
                data-testid="schedule-start-time"
                @update:model-value="(value) => update({ startTime: value })"
            />
            <p v-if="errorFor('startTime')" :class="errorClass">{{ errorFor('startTime') }}</p>
        </ScheduleFormField>
        <ScheduleFormField
            class="shrink-0"
            label="End time"
            name="endTime"
            :bulk="isBulk"
            :changed="isChanged('endTime')"
            @update:changed="(value) => setChanged('endTime', value)"
        >
            <template #label>
                End time
                <span v-if="endsNextDay && !isBulk" class="text-muted-foreground ml-1 text-xs font-normal" data-testid="schedule-ends-next-day">ends next day</span>
            </template>
            <TimeOfDayInput
                :model-value="form.endTime"
                label="End time"
                :invalid="Boolean(errorFor('endTime'))"
                :disabled="readOnly"
                data-testid="schedule-end-time"
                @update:model-value="(value) => update({ endTime: value })"
            />
            <p v-if="errorFor('endTime')" :class="errorClass">{{ errorFor('endTime') }}</p>
        </ScheduleFormField>
    </div>

    <!-- One schedule: the days and "Repeat every" read as one field. -->
    <div v-if="showsDays && !isBulk" class="flex flex-col gap-1.5 sm:col-span-2">
        <span :class="labelClass">Repeat on</span>
        <ScheduleDayPicker :model-value="form.dayIds" :disabled="readOnly" @update:model-value="(value) => update({ dayIds: value })" />
        <p v-if="errorFor('dayIds')" :class="errorClass">{{ errorFor('dayIds') }}</p>
        <div class="flex items-center gap-2 text-sm">
            <label for="schedule-repeat-weeks">Repeat every</label>
            <Input
                id="schedule-repeat-weeks"
                type="number"
                min="1"
                class="w-20"
                :model-value="form.repeatWeeks"
                data-testid="schedule-repeat-weeks"
                @input="setCount('repeatWeeks', $event)"
            />
            <span>{{ form.repeatWeeks === 1 ? 'week' : 'weeks' }}</span>
        </div>
        <p v-if="errorFor('repeatWeeks')" :class="errorClass">{{ errorFor('repeatWeeks') }}</p>
    </div>
    <!-- A bulk edit: each can be kept or changed on its own. -->
    <template v-else-if="showsDays">
        <ScheduleFormField
            class="sm:col-span-2"
            label="Repeat on"
            name="dayIds"
            :bulk="isBulk"
            :changed="isChanged('dayIds')"
            @update:changed="(value) => setChanged('dayIds', value)"
        >
            <ScheduleDayPicker :model-value="form.dayIds" test-id-prefix="schedule-bulk" @update:model-value="(value) => update({ dayIds: value })" />
            <p v-if="errorFor('dayIds')" :class="errorClass">{{ errorFor('dayIds') }}</p>
        </ScheduleFormField>
        <ScheduleFormField
            label="Repeat every"
            for-id="schedule-repeat-weeks"
            name="repeatWeeks"
            :bulk="isBulk"
            :changed="isChanged('repeatWeeks')"
            @update:changed="(value) => setChanged('repeatWeeks', value)"
        >
            <div class="flex items-center gap-2 text-sm">
                <Input
                    id="schedule-repeat-weeks"
                    type="number"
                    min="1"
                    class="w-20"
                    :model-value="form.repeatWeeks"
                    data-testid="schedule-repeat-weeks"
                    @input="setCount('repeatWeeks', $event)"
                />
                <span>{{ form.repeatWeeks === 1 ? 'week' : 'weeks' }}</span>
            </div>
            <p v-if="errorFor('repeatWeeks')" :class="errorClass">{{ errorFor('repeatWeeks') }}</p>
        </ScheduleFormField>
    </template>

    <ScheduleFormField
        :class="isBulk && !showsDays ? 'sm:col-start-1' : ''"
        label="Start date"
        name="startDate"
        :bulk="isBulk"
        :changed="isChanged('startDate')"
        @update:changed="(value) => setChanged('startDate', value)"
    >
        <Select
            v-if="isBulk"
            :model-value="bulkState!.startDateAction"
            @update:model-value="(value) => updateBulk({ startDateAction: value as ScheduleBulkState['startDateAction'] })"
        >
            <SelectTrigger class="w-full" data-testid="schedule-bulk-start-date-action"><SelectValue /></SelectTrigger>
            <SelectContent>
                <SelectItem v-for="action in startDateActions" :key="action.value" :value="action.value">{{ action.label }}</SelectItem>
            </SelectContent>
        </Select>
        <div v-if="isBulk && bulkState!.startDateAction === 'shift'" class="flex items-center gap-2 text-sm">
            <Input
                type="number"
                step="1"
                min="-366"
                max="366"
                placeholder="7"
                class="w-24"
                aria-label="Move the start date by days"
                :model-value="bulkState!.startShiftDays ?? ''"
                data-testid="schedule-bulk-start-shift"
                @input="setShift('startShiftDays', $event)"
            />
            <span class="text-muted-foreground text-xs">days. Use a minus sign to move back.</span>
        </div>
        <DatePicker v-else :model-value="form.startDate" label="Start date" @update:model-value="(value) => update({ startDate: value ?? null })" />
        <p v-if="errorFor('startDate')" :class="errorClass">{{ errorFor('startDate') }}</p>
    </ScheduleFormField>
    <ScheduleFormField
        label="End date"
        name="endDate"
        :bulk="isBulk"
        :changed="isChanged('endDate')"
        @update:changed="(value) => setChanged('endDate', value)"
    >
        <template v-if="isBulk">
            <Select
                :model-value="bulkState!.endDateAction"
                @update:model-value="(value) => updateBulk({ endDateAction: value as ScheduleBulkState['endDateAction'] })"
            >
                <SelectTrigger class="w-full" data-testid="schedule-bulk-end-date-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem v-for="action in endDateActions" :key="action.value" :value="action.value">{{ action.label }}</SelectItem>
                </SelectContent>
            </Select>
            <div v-if="bulkState!.endDateAction === 'shift'" class="flex items-center gap-2 text-sm">
                <Input
                    type="number"
                    step="1"
                    min="-366"
                    max="366"
                    placeholder="7"
                    class="w-24"
                    aria-label="Move the end date by days"
                    :model-value="bulkState!.endShiftDays ?? ''"
                    data-testid="schedule-bulk-end-shift"
                    @input="setShift('endShiftDays', $event)"
                />
                <span class="text-muted-foreground text-xs">days. Use a minus sign to move back.</span>
            </div>
            <DatePicker
                v-else-if="bulkState!.endDateAction === 'set'"
                :model-value="form.endDate"
                :min="endDateMin"
                label="End date"
                @update:model-value="(value) => update({ endDate: value ?? null })"
            />
        </template>
        <template v-else>
            <!-- A one-off has no "No end date" box, so it always shows the picker: a
                 legacy one-off saved without an end date can be given one here, and
                 picking a date ends the no-end-date state. Left empty, it stays open-ended. -->
            <DatePicker
                v-if="!form.noEndDate || !showsNoEndDate"
                :model-value="form.endDate"
                :min="endDateMin"
                label="End date"
                @update:model-value="(value) => update({ endDate: value ?? null, noEndDate: latestForm.noEndDate && !value })"
            />
            <div v-if="showsNoEndDate" class="flex items-center gap-1.5 text-sm">
                <Checkbox
                    id="schedule-no-end-date"
                    :model-value="form.noEndDate"
                    :disabled="readOnly"
                    data-testid="schedule-no-end-date"
                    @update:model-value="(value) => update({ noEndDate: value === true })"
                />
                <label for="schedule-no-end-date" class="cursor-pointer">No end date</label>
            </div>
        </template>
        <p v-if="endDateMessage" :class="errorClass" data-testid="schedule-end-date-error">{{ endDateMessage }}</p>
    </ScheduleFormField>

    <!-- Fields the single editor does not show. A bulk edit can still set them. -->
    <template v-if="isBulk">
        <p class="text-muted-foreground border-t pt-3 text-xs font-medium tracking-wider uppercase sm:col-span-2">More</p>
        <ScheduleFormField
            v-for="flag in (['completeTrack', 'ignoreDlWindow', 'revertToDaily'] as const)"
            :key="flag"
            :label="{ completeTrack: 'Complete track', ignoreDlWindow: 'Ignore DL window', revertToDaily: 'Revert to daily' }[flag]"
            :name="flag"
            :bulk="isBulk"
            :changed="isChanged(flag)"
            @update:changed="(value) => setChanged(flag, value)"
        >
            <Select :model-value="String(bulkState![flag])" @update:model-value="(value) => updateBulk({ [flag]: value === 'true' })">
                <SelectTrigger class="w-full" :data-testid="`schedule-bulk-${flag}`"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem v-for="option in YES_NO" :key="option.value" :value="option.value">{{ option.label }}</SelectItem>
                </SelectContent>
            </Select>
        </ScheduleFormField>
        <ScheduleFormField
            v-for="expression in (['startExpression', 'endExpression'] as const)"
            :key="expression"
            :label="expression === 'startExpression' ? 'Start date expression' : 'End date expression'"
            :name="expression"
            :bulk="isBulk"
            :changed="isChanged(expression)"
            @update:changed="(value) => setChanged(expression, value)"
        >
            <Input
                :model-value="bulkState![expression]"
                maxlength="500"
                placeholder="Leave empty to clear it"
                :data-testid="`schedule-bulk-${expression}`"
                @update:model-value="(value) => updateBulk({ [expression]: String(value) })"
            />
        </ScheduleFormField>
    </template>
</template>
